document.addEventListener("DOMContentLoaded", () => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // --- Preloader ---
  const preloader = document.getElementById("bbdPreloader");
  if (preloader) {
    document.body.classList.add("bbd-preload-lock");

    const minDelay = new Promise((resolve) => setTimeout(resolve, reduceMotion ? 0 : 600));
    const pageLoad = new Promise((resolve) => {
      if (document.readyState === "complete") resolve();
      else window.addEventListener("load", resolve, { once: true });
    });
    const failsafe = new Promise((resolve) => setTimeout(resolve, 5000));

    Promise.race([Promise.all([minDelay, pageLoad]), failsafe]).then(() => {
      preloader.classList.add("is-hidden");
      document.body.classList.remove("bbd-preload-lock");
      setTimeout(() => preloader.remove(), reduceMotion ? 300 : 700);
    });
  }

  // --- Hero: orbiting-channels canvas diagram ---
  const stage = document.getElementById("bbdHeroStage");
  const canvas = document.getElementById("bbdHeroCanvas");
  const label = document.getElementById("bbdHeroLabel");

  if (stage && canvas) {
    const ctx = canvas.getContext("2d");

    const readAccent = () => getComputedStyle(document.documentElement).getPropertyValue("--bbd-beacon").trim() || "#FF5B04";
    const hex = (h) => ({
      r: parseInt(h.substr(1, 2), 16),
      g: parseInt(h.substr(3, 2), 16),
      b: parseInt(h.substr(5, 2), 16),
    });
    const ACC = hex(readAccent());
    const A = (a) => `rgba(${ACC.r},${ACC.g},${ACC.b},${a})`;

    const CH = [
      { id: "CH.01", name: "Paid Search", metric: "ROAS 5.2×", ring: 0, phase: 0.0 },
      { id: "CH.02", name: "Paid Social", metric: "CPA $38.10", ring: 0, phase: 2.1 },
      { id: "CH.03", name: "Affiliate", metric: "EPC $1.44", ring: 0, phase: 4.2 },
      { id: "CH.04", name: "Email / CRM", metric: "RPM $412", ring: 1, phase: 0.9 },
      { id: "CH.05", name: "SEO", metric: "+18% NR", ring: 1, phase: 3.2 },
      { id: "CH.06", name: "Creative", metric: "CTR 3.9%", ring: 1, phase: 5.4 },
      { id: "CH.07", name: "CRO", metric: "CVR 4.1%", ring: 2, phase: 1.6 },
      { id: "CH.08", name: "Analytics", metric: "99.4% attr", ring: 2, phase: 4.6 },
    ];
    const RINGS = [
      { r: 2.05, tilt: 0.30, spin: 0.24 },
      { r: 2.75, tilt: -0.46, spin: -0.17 },
      { r: 3.45, tilt: 0.62, spin: 0.12 },
    ];
    const V = [[-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1], [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1]];
    const FACES = [
      { v: [0, 1, 2, 3], n: [0, 0, 1] }, { v: [5, 4, 7, 6], n: [0, 0, -1] },
      { v: [4, 0, 3, 7], n: [-1, 0, 0] }, { v: [1, 5, 6, 2], n: [1, 0, 0] },
      { v: [3, 2, 6, 7], n: [0, 1, 0] }, { v: [4, 5, 1, 0], n: [0, -1, 0] },
    ];

    let W = 0, H = 0, t = 0, open = 0, hovered = -1, timeScale = 1;
    const mouse = { x: -999, y: -999 };
    const nodePos = CH.map(() => ({ x: 0, y: 0, z: 0, f: 1 }));

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = stage.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const rot = (p, ax, ay) => {
      let x = p[0], y = p[1], z = p[2];
      let c = Math.cos(ay), s = Math.sin(ay);
      const x1 = x * c + z * s, z1 = -x * s + z * c;
      c = Math.cos(ax); s = Math.sin(ax);
      return [x1, y * c - z1 * s, y * s + z1 * c];
    };
    const proj = (p, scale) => {
      const d = 6.2, f = d / (d - p[2]);
      return { x: W / 2 + p[0] * scale * f, y: H / 2 + p[1] * scale * f, z: p[2], f };
    };

    const draw = () => {
      const mobile = W < 600;
      const scale = Math.min(W, H) / (mobile ? 7.6 : 5.6);
      const ax = 0.34 + Math.sin(t * 0.17) * 0.06;
      const ay = t * 0.22;
      ctx.clearRect(0, 0, W, H);
      const rings = mobile ? RINGS.slice(0, 2) : RINGS;
      const dim = hovered >= 0 ? 0.32 : 1;

      rings.forEach((ring) => {
        ctx.beginPath();
        for (let i = 0; i <= 72; i++) {
          const a = (i / 72) * Math.PI * 2;
          const rr = ring.r * (1 - open * 0.18);
          let p = rot([Math.cos(a) * rr, 0, Math.sin(a) * rr], ring.tilt, ring.spin * t);
          p = rot(p, ax, ay);
          const s = proj(p, scale);
          i ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(110,110,120,${0.30 * dim})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      CH.forEach((c, i) => {
        const ring = rings[c.ring % rings.length];
        const a = c.phase + t * (0.30 + ring.spin * 0.4) * (ring.spin > 0 ? 1 : -1);
        const rr = ring.r * (1 - open * 0.18);
        let p = rot([Math.cos(a) * rr, 0, Math.sin(a) * rr], ring.tilt, ring.spin * t);
        p = rot(p, ax, ay);
        const s = proj(p, scale);
        nodePos[i].x = s.x;
        nodePos[i].y = s.y;
        nodePos[i].z = p[2];
        nodePos[i].f = s.f;
      });

      if (!mobile && !reduceMotion) {
        CH.forEach((c, i) => {
          for (let k = 0; k < 2; k++) {
            const prog = (t * 0.34 + i * 0.19 + k * 0.5) % 1;
            const px = nodePos[i].x + (W / 2 - nodePos[i].x) * prog;
            const py = nodePos[i].y + (H / 2 - nodePos[i].y) * prog;
            const alpha = Math.sin(prog * Math.PI) * 0.75 * dim * (hovered === i ? 2.2 : 1);
            ctx.beginPath();
            ctx.arc(px, py, 1.4, 0, Math.PI * 2);
            ctx.fillStyle = hovered === i ? A(alpha) : `rgba(180,180,190,${alpha})`;
            ctx.fill();
          }
        });
      }

      const coreR = 10 + open * 13;
      if (open > 0.02) {
        const g = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, coreR * 4.2);
        g.addColorStop(0, A(0.55 * open));
        g.addColorStop(1, A(0));
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }
      ctx.beginPath();
      ctx.rect(W / 2 - coreR / 2, H / 2 - coreR / 2, coreR, coreR);
      ctx.fillStyle = open > 0.02 ? A(0.35 + open * 0.65) : "rgba(38,38,43,1)";
      ctx.fill();

      FACES.map((f) => {
        const off = open * 0.62;
        const pts = f.v.map((vi) => rot(V[vi].map((c, k) => c + f.n[k] * off), ax, ay));
        return { pts: pts.map((p) => proj(p, scale)), az: pts.reduce((s, p) => s + p[2], 0) / 4 };
      }).sort((a, b) => a.az - b.az).forEach((f) => {
        const a = (0.30 + ((f.az + 1.9) / 3.8) * 0.70) * dim;
        ctx.beginPath();
        f.pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        ctx.fillStyle = `rgba(20,20,23,${0.55 * (1 - open * 0.75)})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(237,237,239,${a})`;
        ctx.lineWidth = 1.35;
        ctx.stroke();
      });

      CH.forEach((c, i) => {
        const n = nodePos[i], on = hovered === i;
        const r = (on ? 6.2 : 3.4) * (n.f * 0.72);
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = on ? A(1) : `rgba(237,237,239,${0.78 * dim})`;
        ctx.fill();
        if (on) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, r + 7, 0, Math.PI * 2);
          ctx.strokeStyle = A(0.45);
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });
    };

    const hitTest = () => {
      let best = -1, bd = 26;
      nodePos.forEach((n, i) => {
        const d = Math.hypot(n.x - mouse.x, n.y - mouse.y);
        if (d < bd) { bd = d; best = i; }
      });
      if (best !== hovered) {
        hovered = best;
        if (label) {
          if (best >= 0) {
            label.innerHTML = `${CH[best].name}<small style="display:block;color:#6E6E78;font-size:10px">${CH[best].id} &nbsp;&middot;&nbsp; ${CH[best].metric}</small>`;
            label.style.opacity = "1";
            label.style.transform = "translateY(0)";
          } else {
            label.style.opacity = "0";
            label.style.transform = "translateY(3px)";
          }
        }
      }
      if (hovered >= 0 && label) {
        label.style.left = `${Math.min(W - 160, nodePos[hovered].x + 14)}px`;
        label.style.top = `${nodePos[hovered].y - 10}px`;
      }
    };

    canvas.addEventListener("pointermove", (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    });
    canvas.addEventListener("pointerleave", () => {
      mouse.x = -999;
      mouse.y = -999;
    });

    const ro = new ResizeObserver(() => { resize(); draw(); });
    ro.observe(stage);
    resize();

    if (reduceMotion) {
      open = 0.85;
      t = 1.2;
      draw();
    } else {
      let last = performance.now();
      const loop = (now) => {
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        if (W < 2 || H < 2) resize();
        const target = Math.max(0, Math.min(1, (window.scrollY || 0) / Math.max(1, window.innerHeight * 0.85)));
        open += (target - open) * Math.min(dt * 5, 1);
        timeScale += ((hovered >= 0 ? 0.12 : 1) - timeScale) * Math.min(dt * 6, 1);
        t += dt * timeScale;
        hitTest();
        draw();
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    }
  }

  // --- Count-up animation for stat and metric values ---
  const countEls = document.querySelectorAll(".bbd-stats__value, .bbd-work__metric-value");
  if (countEls.length) {
    const parseCount = (text) => {
      const match = text.trim().match(/^([^\d]*)([\d,]*\.?\d+)(.*)$/);
      if (!match) return null;
      const [, prefix, rawNum, suffix] = match;
      const hasComma = rawNum.includes(",");
      const cleanNum = rawNum.replace(/,/g, "");
      const decimals = cleanNum.includes(".") ? cleanNum.split(".")[1].length : 0;
      const target = parseFloat(cleanNum);
      if (Number.isNaN(target)) return null;
      return { prefix, target, decimals, suffix, hasComma };
    };

    const formatCount = (value, decimals, hasComma) => {
      const fixed = value.toFixed(decimals);
      if (!hasComma) return fixed;
      const [intPart, decPart] = fixed.split(".");
      const withCommas = Number(intPart).toLocaleString("en-US");
      return decPart ? `${withCommas}.${decPart}` : withCommas;
    };

    const animateCount = (el) => {
      const parsed = parseCount(el.textContent);
      if (!parsed) return;
      const { prefix, target, decimals, suffix, hasComma } = parsed;
      if (reduceMotion) {
        el.textContent = `${prefix}${formatCount(target, decimals, hasComma)}${suffix}`;
        return;
      }
      const duration = 1300;
      let start = null;
      const step = (now) => {
        if (start === null) start = now;
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = `${prefix}${formatCount(target * eased, decimals, hasComma)}${suffix}`;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const countObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          countObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    countEls.forEach((el) => countObserver.observe(el));
  }

  // --- Slow fade-up reveal for text blocks ---
  const revealSelector = [
    ".bbd-hero__title",
    ".bbd-hero__text",
    ".bbd-hero__metric",
    ".bbd-stats__value",
    ".bbd-stats__label",
    ".bbd-eyebrow",
    ".bbd-heading",
    ".bbd-lede",
    ".bbd-channels__id",
    ".bbd-channels__name",
    ".bbd-channels__scope",
    ".bbd-channels__metric",
    ".bbd-work__title",
    ".bbd-work__client",
    ".bbd-work__headline",
    ".bbd-work__desc",
    ".bbd-work__metric-label",
    ".bbd-work__metric-value",
    ".bbd-work__flow",
    ".bbd-reporting__card-title",
    ".bbd-reporting__card-text",
    ".bbd-contact__title",
    ".bbd-contact__text",
    ".bbd-contact__meta",
  ].join(", ");
  const revealEls = document.querySelectorAll(revealSelector);
  if (revealEls.length) {
    if (reduceMotion) {
      revealEls.forEach((el) => el.classList.add("is-visible"));
    } else {
      revealEls.forEach((el) => el.classList.add("bbd-reveal"));
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });
      revealEls.forEach((el) => revealObserver.observe(el));
    }
  }

  // --- Reporting: typed readout lines ---
  const readout = document.getElementById("bbdReadout");
  if (readout && !reduceMotion) {
    const lines = Array.from(readout.querySelectorAll("[data-line]"));
    if (lines.length) {
      const widths = lines.map((el) => el.getBoundingClientRect().width);
      lines.forEach((el) => { el.style.width = "0px"; el.style.visibility = "hidden"; });
      const durations = [1100, 900, 950];
      let i = 0, start = null, done = false;
      const finish = () => {
        if (done) return;
        done = true;
        lines.forEach((el) => { el.style.width = ""; el.style.visibility = ""; });
      };
      const guard = setTimeout(finish, 6000);
      const step = (now) => {
        if (done) return;
        if (i >= lines.length) { clearTimeout(guard); finish(); return; }
        if (start === null) start = now;
        const el = lines[i];
        const chars = Math.max(1, (el.textContent || "").length);
        const p = Math.min(1, (now - start) / durations[i]);
        el.style.visibility = "visible";
        el.style.width = `${widths[i] * (Math.round(p * chars) / chars)}px`;
        if (p >= 1) { el.style.width = ""; i += 1; start = null; }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }

  // --- Contact form (Contact Form 7) ---
  const contactWrap = document.getElementById("bbdContactFormWrap");
  if (contactWrap) {
    const cf7Form = contactWrap.querySelector(".wpcf7-form");
    const success = document.getElementById("bbdContactSuccess");

    if (cf7Form) {
      cf7Form.addEventListener("wpcf7mailsent", () => {
        cf7Form.hidden = true;
        success.hidden = false;
      });
    }
  }

  // --- Slow smooth scroll to in-page anchors (accounts for the fixed header) ---
  const headerOffset = 88;

  const scrollToTarget = (target) => {
    const startY = window.scrollY;
    const targetY = Math.max(0, target.getBoundingClientRect().top + startY - headerOffset);

    if (reduceMotion) {
      window.scrollTo({ top: targetY, left: 0, behavior: "auto" });
      return;
    }

    const duration = 900;
    let start = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      if (start === null) start = now;
      const p = Math.min(1, (now - start) / duration);
      window.scrollTo({ top: startY + (targetY - startY) * ease(p), left: 0, behavior: "auto" });
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      const href = anchor.getAttribute("href");
      if (!href || href.length < 2) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      scrollToTarget(target);
    });
  });

  if (window.location.hash) {
    const initialTarget = document.querySelector(window.location.hash);
    if (initialTarget) {
      setTimeout(() => {
        const top = Math.max(0, initialTarget.getBoundingClientRect().top + window.scrollY - headerOffset);
        window.scrollTo({ top, left: 0, behavior: "auto" });
      }, 300);
    }
  }
});
